import React, { useState, useRef } from 'react';
import { useLanguage, api } from '../context/AppContext';
import { Layout } from '../components/Layout';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { Loader2, Camera, ShieldAlert, ChefHat, Clock, Flame, ArrowLeft, Target } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';

const SOSCraving = () => {
  const { language, t } = useLanguage();
  const navigate = useNavigate();
  const fr = language === 'fr';
  const fileRef = useRef(null);
  const [analyzing, setAnalyzing] = useState(false);
  const [result, setResult] = useState(null);
  const [preview, setPreview] = useState(null);

  const handleCapture = (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = async (ev) => {
      const base64 = ev.target.result.split(',')[1];
      setPreview(ev.target.result);
      setAnalyzing(true);
      try {
        const res = await api.post('/sos-craving/analyze', { image_base64: base64 });
        setResult(res.data);
      } catch {
        toast.error(t('pages.sos_analysis_error'));
      } finally {
        setAnalyzing(false);
      }
    };
    reader.readAsDataURL(file);
  };

  return (
    <Layout>
      <div className="p-4 md:p-8 max-w-lg mx-auto space-y-4" data-testid="sos-craving-page">
        {/* Header */}
        <div className="flex items-center gap-3">
          <button onClick={() => navigate(-1)} className="text-muted-foreground hover:text-foreground">
            <ArrowLeft className="w-5 h-5" />
          </button>
          <div>
            <h1 className="font-heading text-2xl font-black uppercase tracking-tight text-red-400">
              {t('pages.sos_title')}
            </h1>
            <p className="text-xs text-muted-foreground">
              {t('pages.sos_subtitle')}
            </p>
          </div>
        </div>

        {/* Capture zone */}
        {!result && !analyzing && (
          <Card className="bg-card border-2 border-red-500/30 border-dashed">
            <CardContent className="p-8 flex flex-col items-center gap-4">
              <div className="w-20 h-20 rounded-full bg-red-500/15 flex items-center justify-center">
                <ShieldAlert className="w-10 h-10 text-red-400" />
              </div>
              <p className="text-center text-sm text-muted-foreground">
                {fr
                  ? 'Prends une photo de ton frigo ou garde-manger. Le Coach va identifier les aliments sains et te proposer un repas rapide.'
                  : 'Take a photo of your fridge or pantry. Coach will identify healthy foods and suggest a quick meal.'}
              </p>
              <input
                ref={fileRef}
                type="file"
                accept="image/*"
                capture="environment"
                onChange={handleCapture}
                className="hidden"
                data-testid="sos-camera-input"
              />
              <Button
                onClick={() => fileRef.current?.click()}
                className="bg-red-500 hover:bg-red-600 text-white w-full h-14 text-lg font-heading uppercase"
                data-testid="sos-capture-btn"
              >
                <Camera className="w-6 h-6 mr-3" />
                {t('pages.sos_camera')}
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Analyzing */}
        {analyzing && (
          <Card className="bg-card border-red-500/30">
            <CardContent className="p-8 flex flex-col items-center gap-3">
              {preview && (
                <img src={preview} alt="Fridge" className="w-full max-h-48 object-cover rounded-lg mb-2 opacity-60" />
              )}
              <Loader2 className="w-12 h-12 text-red-400 animate-spin" />
              <p className="text-sm text-muted-foreground animate-pulse">
                {fr ? 'Le Coach analyse ton frigo...' : 'Coach is analyzing your fridge...'}
              </p>
            </CardContent>
          </Card>
        )}

        {/* Result */}
        {result && (
          <div className="space-y-4" data-testid="sos-result">
            {/* Preview */}
            {preview && (
              <img src={preview} alt="Fridge" className="w-full h-40 object-cover rounded-xl" />
            )}

            {/* Available foods */}
            {result.available_foods?.length > 0 && (
              <div className="flex flex-wrap gap-1.5">
                {result.available_foods.map((food, i) => (
                  <span key={i} className="px-2.5 py-1 rounded-full text-[11px] bg-emerald-500/15 border border-emerald-500/30 text-emerald-400">
                    {food}
                  </span>
                ))}
              </div>
            )}

            {/* Suggested meal */}
            <Card className="bg-gradient-to-br from-emerald-500/10 to-card border-emerald-500/30">
              <CardContent className="p-4 space-y-3">
                <div className="flex items-center gap-2">
                  <ChefHat className="w-5 h-5 text-emerald-400" />
                  <h3 className="font-heading text-sm uppercase tracking-wider text-emerald-400">
                    {result.suggested_meal || (fr ? 'Repas suggere' : 'Suggested meal')}
                  </h3>
                </div>
                <p className="text-sm text-foreground leading-relaxed whitespace-pre-line">
                  {result.recipe}
                </p>
                <div className="flex gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1"><Clock className="w-3 h-3" /> {result.prep_time}</span>
                  <span className="flex items-center gap-1"><Target className="w-3 h-3 text-primary" /> {result.estimated_protein}g P</span>
                  <span className="flex items-center gap-1"><Flame className="w-3 h-3 text-orange-400" /> {result.estimated_calories} cal</span>
                </div>
              </CardContent>
            </Card>

            {/* Coach message */}
            {result.coach_message && (
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                <p className="text-xs text-primary leading-relaxed">
                  {result.coach_message}
                </p>
              </div>
            )}

            {/* Actions */}
            <div className="flex gap-2">
              <Button
                onClick={() => { setResult(null); setPreview(null); }}
                variant="outline"
                className="flex-1"
                data-testid="sos-retake-btn"
              >
                <Camera className="w-4 h-4 mr-2" />
                {fr ? 'Reprendre' : 'Retake'}
              </Button>
              <Button
                onClick={() => navigate('/dashboard')}
                className="flex-1 bg-primary text-primary-foreground"
                data-testid="sos-done-btn"
              >
                {fr ? 'Mission accomplie' : 'Mission complete'}
              </Button>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};

export default SOSCraving;
